"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import ProjectTimeline from "./ProjectTimeline";
import {
  PencilSquareIcon,
  MapPinIcon,
  EnvelopeIcon,
} from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";

const fetchContactsForAccounts = async (accountIds) => {
  const token = localStorage.getItem("token");
  const ids = [...new Set(accountIds.map(idOf).filter(Boolean))];
  if (ids.length === 0) return [];
  const results = await Promise.all(
    ids.map((id) =>
      axios.get(
        `${API}/contacts/related?relatedAccount=${id}&populate=relatedAccount`,
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    ),
  );
  const merged = new Map();
  results.forEach((r) => r.data.forEach((c) => merged.set(c._id, c)));
  return Array.from(merged.values());
};

const buildFormData = (p) => ({
  projectName: p.projectName || "",
  accountId: idOf(p.accountId),
  address: {
    formattedAddress: p.address?.formattedAddress || "",
    street: p.address?.street || "",
    city: p.address?.city || "",
    state: p.address?.state || "",
    postalCode: p.address?.postalCode || "",
    country: p.address?.country || "",
    latitude: p.address?.latitude || null,
    longitude: p.address?.longitude || null,
  },
  status: p.status || "planning",
  projectManager: idOf(p.projectManager),
  foreman: idOf(p.foreman),
  companyName: p.accountId?.companyName || "",
  customerContact: idOf(p.customerContact),
  billingContact: idOf(p.billingContact),
  sourceCategory: p.sourceCategory || "",
  constructionType: p.constructionType || "",
  marketSegment: p.marketSegment || "",
});

const LEAD_SOURCE_CATEGORIES = [
  "Public Tender Platform",
  "Direct Invite",
  "Referral",
  "Outbound Prospecting",
  "Repeat Client",
];

const CONSTRUCTION_TYPE_OPTIONS = [
  { value: "", label: "None" },
  { value: "Renovation", label: "Renovation" },
  { value: "New Build", label: "New Build" },
  { value: "Mixed", label: "Mixed (Renovation & New Build)" },
];

const MARKET_SEGMENT_OPTIONS = [
  { value: "", label: "None" },
  { value: "Commercial", label: "Commercial" },
  { value: "Institutional", label: "Institutional" },
  {
    value: "Multi-Residential/High-Rise",
    label: "Multi-Residential/High-Rise",
  },
  { value: "Industrial", label: "Industrial" },
  { value: "Other", label: "Other" },
];

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  clearIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 30,
  }),
};

const inputClass =
  "block w-full rounded-lg bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none";

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors";

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  blue: {
    wrap: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
    dot: "fill-blue-400",
  },
};

function Badge({ tone = "yellow", children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      <svg
        viewBox="0 0 6 6"
        aria-hidden="true"
        className={classNames("size-1.5", t.dot)}
      >
        <circle r={3} cx={3} cy={3} />
      </svg>
      {children}
    </span>
  );
}

const statusTone = (s) =>
  s === "active" ? "green" : s === "completed" ? "blue" : "yellow";

function Section({ title, action, children, className = "" }) {
  return (
    <section
      className={classNames(
        "rounded-xl bg-gray-800 ring-1 ring-white/10",
        className,
      )}
    >
      {title && (
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-orange-400">
            {title}
          </h3>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
      {children}
    </label>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm text-gray-100">{children}</dd>
    </div>
  );
}

const contactName = (contact) =>
  contact
    ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
      contact.name ||
      "Unnamed Contact"
    : "Not Assigned";

export default function GeneralTab({ project: initialProject, setProject }) {
  const [isEditing, setIsEditing] = useState(false);
  const [project, setLocalProject] = useState(initialProject);
  const [formData, setFormData] = useState(() => buildFormData(initialProject));
  const [users, setUsers] = useState({
    projectManagerUsers: [],
    foremanUsers: [],
  });
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);

  const initialAccountId = idOf(initialProject.accountId);
  const initialBillingAccountId = idOf(initialProject.billingAccount);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const usersResponse = await axios.get(`${API}/users`, { headers });
        setUsers({
          projectManagerUsers: usersResponse.data.filter((u) =>
            ["admin", "project_manager"].includes(u.role),
          ),
          foremanUsers: usersResponse.data.filter((u) =>
            ["foreman", "manager"].includes(u.role),
          ),
        });

        const accountsResponse = await axios.get(`${API}/accounts`, {
          headers,
        });
        setAccounts(accountsResponse.data);

        const merged = await fetchContactsForAccounts([
          initialAccountId,
          initialBillingAccountId,
        ]);
        // Make sure the saved contacts are always resolvable for display
        [initialProject.customerContact, initialProject.billingContact].forEach(
          (c) => {
            if (c && typeof c === "object" && c._id) {
              if (!merged.some((m) => m._id === c._id)) merged.push(c);
            }
          },
        );
        setContacts(merged);
      } catch (err) {
        setError("Failed to load initial data.");
        console.error("Error fetching initial data:", err.message);
      }
    };
    fetchInitialData();
  }, [
    initialAccountId,
    initialBillingAccountId,
    initialProject.customerContact,
    initialProject.billingContact,
  ]);

  useEffect(() => {
    if (!isEditing) return;

    if (!formData.accountId) {
      setContacts([]);
      setFormData((prev) => ({
        ...prev,
        customerContact: "",
        billingContact: "",
      }));
      return;
    }

    const loadContacts = async () => {
      try {
        const merged = await fetchContactsForAccounts([
          formData.accountId,
          project.billingAccount,
        ]);

        // Keep the currently saved contacts in the list even if they come from another account
        [project.customerContact, project.billingContact].forEach((c) => {
          if (c && typeof c === "object" && c._id) {
            if (!merged.some((m) => m._id === c._id)) merged.push(c);
          }
        });

        setContacts(merged);

        setFormData((prev) => ({
          ...prev,
          customerContact:
            prev.customerContact &&
            merged.some((c) => c._id === prev.customerContact)
              ? prev.customerContact
              : "",
          billingContact:
            prev.billingContact &&
            merged.some((c) => c._id === prev.billingContact)
              ? prev.billingContact
              : "",
        }));
      } catch (err) {
        setError("Failed to load contacts.");
        console.error("Error fetching contacts:", err.message);
      }
    };
    loadContacts();
  }, [formData.accountId, isEditing]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/projects/${initialProject._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const updatedProject = response.data;
      setLocalProject(updatedProject);
      setProject(updatedProject);
      setFormData(buildFormData(updatedProject));
      setError(null);
    } catch (err) {
      setError("Failed to reload project.");
      console.error(
        "Error fetching project:",
        err.response?.data || err.message,
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("address.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name) => (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleCancel = () => {
    setFormData(buildFormData(project));
    setError(null);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.billingContact) {
      setError("Billing Contact is required.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...formData,
        accountId: idOf(formData.accountId),
        customerContact: idOf(formData.customerContact),
        billingContact: idOf(formData.billingContact),
        projectManager: idOf(formData.projectManager),
        foreman: idOf(formData.foreman),
      };
      await axios.put(`${API}/projects/${project._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchProject();
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update project.");
      console.error(
        "Error updating project:",
        err.response?.data || err.message,
      );
    }
  };

  const projectManagerOptions = users.projectManagerUsers.map((user) => ({
    value: user._id,
    label: user.displayName || "Unnamed User",
  }));

  const foremanOptions = users.foremanUsers.map((user) => ({
    value: user._id,
    label: user.displayName || "Unnamed User",
  }));

  const accountOptions = accounts.map((account) => ({
    value: account._id,
    label: account.companyName || "Unnamed Account",
  }));

  const contactOptions = contacts.map((contact) => ({
    value: contact._id,
    label:
      `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
      "Unnamed Contact",
  }));

  const resolveContact = (ref) =>
    contacts.find((c) => c._id === idOf(ref)) ||
    (ref && typeof ref === "object" ? ref : null);

  const statusLabel =
    (project.status || "planning").charAt(0).toUpperCase() +
    (project.status || "planning").slice(1);

  const overviewAction = !isEditing ? (
    <button onClick={() => setIsEditing(true)} className={btnPrimary}>
      <PencilSquareIcon className="size-4" />
      Edit
    </button>
  ) : (
    <div className="flex gap-2">
      <button type="button" onClick={handleCancel} className={btnSecondary}>
        Cancel
      </button>
      <button type="submit" form="project-general-form" className={btnPrimary}>
        Save Changes
      </button>
    </div>
  );

  const addressText =
    [
      project.address?.street,
      project.address?.city,
      project.address?.state,
      project.address?.postalCode,
      project.address?.country,
    ]
      .filter(Boolean)
      .join(", ") || "Not specified";

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div className="space-y-6 xl:col-span-2">
        {error && (
          <div className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/20">
            {error}
          </div>
        )}

        {isEditing ? (
          <form
            id="project-general-form"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <Section title="Overview" action={overviewAction}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Project Name">
                  <input
                    type="text"
                    name="projectName"
                    value={formData.projectName || ""}
                    onChange={handleInputChange}
                    className={inputClass}
                    required
                  />
                </FormField>
                <FormField label="Status">
                  <select
                    name="status"
                    value={formData.status || "planning"}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="planning">Planning</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </select>
                </FormField>
              </div>
            </Section>

            <Section title="Location">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Street" className="md:col-span-2">
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street || ""}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </FormField>
                <FormField label="City">
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city || ""}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </FormField>
                <FormField label="State / Province">
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state || ""}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </FormField>
                <FormField label="Postal Code">
                  <input
                    type="text"
                    name="address.postalCode"
                    value={formData.address.postalCode || ""}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </FormField>
                <FormField label="Country">
                  <input
                    type="text"
                    name="address.country"
                    value={formData.address.country || ""}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </FormField>
              </div>
            </Section>

            <Section title="Account & Contacts">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Account" className="md:col-span-2">
                  <Select
                    options={accountOptions}
                    value={
                      accountOptions.find(
                        (option) => option.value === formData.accountId,
                      ) || null
                    }
                    onChange={handleSelectChange("accountId")}
                    placeholder="Select Account (Optional)"
                    isClearable
                    styles={customStyles}
                  />
                </FormField>
                <FormField label="Customer Contact">
                  <Select
                    options={contactOptions}
                    value={
                      contactOptions.find(
                        (option) => option.value === formData.customerContact,
                      ) || null
                    }
                    onChange={handleSelectChange("customerContact")}
                    placeholder={
                      formData.accountId
                        ? "Select Customer Contact (Optional)"
                        : "Select an Account first"
                    }
                    isClearable
                    isDisabled={!formData.accountId}
                    styles={customStyles}
                  />
                </FormField>
                <FormField label="Billing Contact *">
                  <Select
                    options={contactOptions}
                    value={
                      contactOptions.find(
                        (option) => option.value === formData.billingContact,
                      ) || null
                    }
                    onChange={handleSelectChange("billingContact")}
                    placeholder={
                      formData.accountId
                        ? "Select Billing Contact"
                        : "Select an Account first"
                    }
                    isDisabled={!formData.accountId}
                    styles={customStyles}
                  />
                </FormField>
              </div>
            </Section>

            <Section title="Team">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Project Manager">
                  <Select
                    options={projectManagerOptions}
                    value={
                      projectManagerOptions.find(
                        (option) => option.value === formData.projectManager,
                      ) || null
                    }
                    onChange={handleSelectChange("projectManager")}
                    placeholder="Select Project Manager (Optional)"
                    isClearable
                    styles={customStyles}
                  />
                </FormField>
                <FormField label="Foreman">
                  <Select
                    options={foremanOptions}
                    value={
                      foremanOptions.find(
                        (option) => option.value === formData.foreman,
                      ) || null
                    }
                    onChange={handleSelectChange("foreman")}
                    placeholder="Select Foreman (Optional)"
                    isClearable
                    styles={customStyles}
                  />
                </FormField>
              </div>
            </Section>

            <Section title="Classification">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField label="Lead Source">
                  <select
                    name="sourceCategory"
                    value={formData.sourceCategory}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select a source...</option>
                    {LEAD_SOURCE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Construction Type">
                  <select
                    name="constructionType"
                    value={formData.constructionType}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    {CONSTRUCTION_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Market Segment">
                  <select
                    name="marketSegment"
                    value={formData.marketSegment}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    {MARKET_SEGMENT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
            </Section>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className={btnSecondary}
              >
                Cancel
              </button>
              <button type="submit" className={btnPrimary}>
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <Section title="Overview" action={overviewAction}>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <Field label="Project Name">
                  <span className="font-medium">
                    {project.projectName || "Unnamed"}
                  </span>
                </Field>
                <Field label="Status">
                  <Badge tone={statusTone(project.status)}>{statusLabel}</Badge>
                </Field>
                <Field label="Email Alias" className="md:col-span-2">
                  {project.aliasEmail ? (
                    <span className="inline-flex items-center gap-2">
                      <EnvelopeIcon className="size-4 shrink-0 text-gray-500" />
                      <span className="break-all">{project.aliasEmail}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">
                      No email alias assigned
                    </span>
                  )}
                </Field>
              </dl>
            </Section>

            <Section title="Location">
              <div className="flex items-start gap-2 text-sm text-gray-100">
                <MapPinIcon className="mt-0.5 size-5 shrink-0 text-orange-400" />
                <span>{addressText}</span>
              </div>
            </Section>

            <Section title="Account & Contacts">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <Field label="Account" className="md:col-span-2">
                  {project.accountId?.companyName ||
                    accounts.find((a) => a._id === idOf(project.accountId))
                      ?.companyName ||
                    "Not Assigned"}
                </Field>
                <Field label="Customer Contact">
                  {contactName(resolveContact(project.customerContact))}
                </Field>
                <Field label="Billing Contact">
                  {contactName(resolveContact(project.billingContact))}
                </Field>
              </dl>
            </Section>

            <Section title="Team">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                <Field label="Project Manager">
                  {project.projectManager?.displayName ||
                    users.projectManagerUsers.find(
                      (u) => u._id === idOf(project.projectManager),
                    )?.displayName ||
                    "Unassigned"}
                </Field>
                <Field label="Foreman">
                  {project.foreman?.displayName ||
                    users.foremanUsers.find(
                      (u) => u._id === idOf(project.foreman),
                    )?.displayName ||
                    "Unassigned"}
                </Field>
              </dl>
            </Section>

            <Section title="Classification">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-3">
                <Field label="Lead Source">
                  {project.sourceCategory || "—"}
                </Field>
                <Field label="Construction Type">
                  {project.constructionType || "—"}
                </Field>
                <Field label="Market Segment">
                  {project.marketSegment || "—"}
                </Field>
              </dl>
            </Section>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="xl:col-span-1">
        <div className="xl:sticky xl:top-4">
          <Section title="Project Timeline">
            <div className="max-h-[calc(100vh-260px)] overflow-y-auto pr-2">
              <ProjectTimeline />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
